import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbX, TbChevronLeft, TbChevronRight } from "react-icons/tb";
import "src/components/Popup/popup.scss";
import "./padFlowStoryboard.scss";

const CARD_W = 220;
const GAP = 36;
// Roughly matches the track's CSS transition duration — long enough that one scroll
// gesture (which can fire many wheel events in a row) only ever advances one step.
const WHEEL_COOLDOWN_MS = 500;

// Step number -> demo clip, for steps that have one recorded yet. mp4, not gif — same
// content (re-encoded from the original gifs, not re-rendered), but H.264 compression gets
// the same visual result at a fraction of the size (e.g. step3 went 5.3MB gif -> 0.9MB mp4),
// which is most of what made these slow to load in the first place.
const STEP_MEDIA: Record<number, string> = {
    1: "/how-it-works-step1.mp4",
    2: "/how-it-works-step2.mp4",
    3: "/how-it-works-step3.mp4",
    4: "/how-it-works-step4.mp4",
    5: "/how-it-works-step5.mp4",
    6: "/how-it-works-step6.mp4",
    7: "/how-it-works-step7.mp4",
    8: "/how-it-works-step8.mp4",
};

type Step = { n: number; title: string; description: string; media?: string };

/** The active step's demo clip, if it has one — a fixed-size area above the carousel so it
 *  stays legible (the carousel cards themselves are too narrow at ~220px to show real UI
 *  detail). Hidden entirely for steps without one yet, rather than leaving empty space.
 *
 *  Still enough of a download that a step switch doesn't show its clip instantly — a dot
 *  loader covers the gap. `loadedSrc` (which src last finished loading) is compared against
 *  the current `src` prop rather than tracked with a plain "isLoading" boolean, so fast step
 *  navigation can't get confused: if the user is already three steps past the one that just
 *  finished loading, that stale load event no longer matches `src` and is correctly ignored.
 *  The `key={src}` remount also means an in-flight load for a step the user has since left
 *  simply never fires its handler on a detached element — nothing to race against. */
function StepMedia({ src }: { src: string | undefined }) {
    const [loadedSrc, setLoadedSrc] = useState<string | null>(null);
    const isLoading = !!src && loadedSrc !== src;

    return (
        <div className={`pad-flow-carousel__media-wrap${src ? "" : " pad-flow-carousel__media-wrap--empty"}`}>
            {src && (
                <>
                    {isLoading && (
                        <div className="pad-flow-carousel__media-loader" aria-hidden="true">
                            <span /><span /><span />
                        </div>
                    )}
                    <video
                        key={src}
                        className={`pad-flow-carousel__media${isLoading ? " pad-flow-carousel__media--loading" : ""}`}
                        src={src}
                        autoPlay
                        loop
                        muted
                        playsInline
                        preload="auto"
                        onLoadedData={() => setLoadedSrc(src)}
                        onError={() => setLoadedSrc(src)}
                    />
                </>
            )}
        </div>
    );
}

/** Horizontal carousel — the active step is always centered at full size; its neighbors
 *  peek in on either side, smaller and faded. A vertical scroll gesture (wheel) advances/
 *  retreats one step at a time, same as arrow keys, dots, or clicking a peeking card. */
function StoryCarousel({ steps }: { steps: Step[] }) {
    const { t } = useTranslation();
    const [activeIndex, setActiveIndex] = useState(0);
    const viewportRef = useRef<HTMLDivElement>(null);
    // Content-box width (clientWidth minus the viewport's own horizontal padding) — the
    // track is centered within that area, not the full padding-inclusive clientWidth.
    const [contentWidth, setContentWidth] = useState(0);
    const wheelLockRef = useRef(false);

    useEffect(() => {
        const el = viewportRef.current;
        if (!el) return;
        const update = () => {
            const style = window.getComputedStyle(el);
            const padL = parseFloat(style.paddingLeft) || 0;
            const padR = parseFloat(style.paddingRight) || 0;
            setContentWidth(el.clientWidth - padL - padR);
        };
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const goTo = (i: number) => setActiveIndex(Math.max(0, Math.min(steps.length - 1, i)));

    // Native (non-passive) wheel listener — React's synthetic onWheel is passive by
    // default, so preventDefault() inside a JSX handler is silently ignored. It's needed
    // here: a vertical scroll gesture drives a horizontal carousel one step at a time, so
    // the gesture must not also scroll the popup itself.
    useEffect(() => {
        const el = viewportRef.current;
        if (!el) return;
        const handler = (e: WheelEvent) => {
            e.preventDefault();
            if (wheelLockRef.current) return;
            if (Math.abs(e.deltaY) < 6) return;
            wheelLockRef.current = true;
            setActiveIndex((i) => {
                const next = e.deltaY > 0 ? i + 1 : i - 1;
                return Math.max(0, Math.min(steps.length - 1, next));
            });
            window.setTimeout(() => { wheelLockRef.current = false; }, WHEEL_COOLDOWN_MS);
        };
        el.addEventListener("wheel", handler, { passive: false });
        return () => el.removeEventListener("wheel", handler);
    }, [steps.length]);

    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (e.key === "ArrowRight" || e.key === "ArrowDown") goTo(activeIndex + 1);
            if (e.key === "ArrowLeft" || e.key === "ArrowUp") goTo(activeIndex - 1);
        };
        document.addEventListener("keydown", handler);
        return () => document.removeEventListener("keydown", handler);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeIndex, steps.length]);

    // Prefetch the immediate neighbors' clips (not the whole set — these run up to ~1MB
    // each, so warming all 8 on open would just compete with the active one for bandwidth).
    // By the time a normal next/prev click lands, the target clip already has a head start
    // instead of starting its fetch from zero at the moment it becomes visible.
    useEffect(() => {
        const neighborSrcs = [steps[activeIndex - 1]?.media, steps[activeIndex + 1]?.media]
            .filter((s): s is string => !!s);
        const links = neighborSrcs.map((href) => {
            const link = document.createElement("link");
            link.rel = "preload";
            link.as = "video";
            link.href = href;
            document.head.appendChild(link);
            return link;
        });
        return () => { links.forEach((link) => link.remove()); };
    }, [activeIndex, steps]);

    const offset = contentWidth > 0
        ? contentWidth / 2 - (activeIndex * (CARD_W + GAP) + CARD_W / 2)
        : 0;

    return (
        <div className="pad-flow-carousel">
            <StepMedia src={steps[activeIndex]?.media} />
            <div className="pad-flow-carousel__viewport" ref={viewportRef}>
                <div className="pad-flow-carousel__track" style={{ transform: `translateX(${offset}px)` }}>
                    {steps.map((s, i) => {
                        const diff = i - activeIndex;
                        const state = diff === 0 ? "active" : Math.abs(diff) === 1 ? "near" : "far";
                        return (
                            <button
                                type="button"
                                key={s.n}
                                data-step={s.n}
                                className={`pad-flow-carousel__card pad-flow-carousel__card--${state}`}
                                style={{ width: CARD_W, marginRight: GAP }}
                                onClick={() => goTo(i)}
                                tabIndex={state === "far" ? -1 : 0}
                            >
                                <span className="pad-flow-carousel__bignum" aria-hidden="true">
                                    {String(s.n).padStart(2, "0")}
                                </span>
                                <h4 className="pad-flow-carousel__title">{s.title}</h4>
                                <p className="pad-flow-carousel__desc">{s.description}</p>
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className="pad-flow-carousel__nav">
                <button
                    type="button"
                    className="pad-flow-carousel__arrow"
                    onClick={() => goTo(activeIndex - 1)}
                    disabled={activeIndex === 0}
                    aria-label="Previous"
                >
                    <TbChevronLeft />
                </button>
                <div className="pad-flow-carousel__dots">
                    {steps.map((s, i) => (
                        <button
                            type="button"
                            key={s.n}
                            className={`pad-flow-carousel__dot${i === activeIndex ? " pad-flow-carousel__dot--active" : ""}`}
                            onClick={() => goTo(i)}
                            aria-label={`${s.n}`}
                        />
                    ))}
                </div>
                <button
                    type="button"
                    className="pad-flow-carousel__arrow"
                    onClick={() => goTo(activeIndex + 1)}
                    disabled={activeIndex === steps.length - 1}
                    aria-label="Next"
                >
                    <TbChevronRight />
                </button>
            </div>
        </div>
    );
}

export function PadFlowStoryboardPopup({ onClose, embedded }: { onClose: () => void; embedded?: boolean }) {
    const { t, i18n } = useTranslation();

    useEffect(() => {
        if (embedded) return;
        const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", handler);
        return () => document.removeEventListener("keydown", handler);
    }, [embedded, onClose]);

    const steps: Step[] = useMemo(
        () =>
            Array.from({ length: 8 }, (_, i) => {
                const n = i + 1;
                return {
                    n,
                    title: t(`fileUpload.flowStep${n}Title`),
                    description: t(`fileUpload.flowStep${n}Desc`),
                    media: STEP_MEDIA[n],
                };
            }),
        [t, i18n.resolvedLanguage],
    );

    if (embedded) {
        return (
            <div className="popup popup--storyboard pad-flow-popup">
                <StoryCarousel steps={steps} />
            </div>
        );
    }

    return (
        <div className="popup-overlay" onClick={onClose}>
            <div
                className="popup popup--storyboard pad-flow-popup"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="pad-flow-storyboard-title"
            >
                <div className="popup__header pad-flow-popup__header">
                    <span id="pad-flow-storyboard-title" className="popup__title">
                        {t("fileUpload.flowStoryboardTitle")}
                    </span>
                    <button type="button" className="popup__close" onClick={onClose} aria-label={t("popup.done")}>
                        <TbX />
                    </button>
                </div>

                <StoryCarousel steps={steps} />

                <div className="popup__actions">
                    <button type="button" className="popup__btn popup__btn--primary" onClick={onClose}>
                        {t("popup.done")}
                    </button>
                </div>
            </div>
        </div>
    );
}
